import { useCallback, useState } from "react"

import { Client, ClientAgencyConfig } from "models/common"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import {
  activateAgencyClient,
  deactivateAgencyClient,
  fetchClientAgencyConfigs,
} from "api/clients"
import { clientColumns, clientConfigColumns, sortByDefault } from "./columns"
import { defaultSort, limitAndOffset } from "api/utils"
import { fetchAgencyClients } from "api/agencies"
import { queryCache, useMutation, useQuery } from "react-query"

import Breadcrumbs from "components/Breadcrumbs"
import { ReactComponent as ClientsIcon } from "assets/icon-client.svg"
import { EmptyState } from "components/EmptyState"
import { Row } from "react-table"
import Surface from "components/Surface"
import { Table } from "components/tables"
import { css } from "@emotion/css"
import { history } from "my-history"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { SelectAgency } from "../../components/SelectAgency"
import { useAgencyId } from "hooks/useAgencyId"

export default function AgencyClientsList() {
  const { t } = useI18n()
  const ls = useLocationSearch()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const isModalOpen = Boolean(modalInfo)
  const { hasAuthority, meta } = useAuthenticated()
  const isTeamLead = meta?.teamLead
  const sendFeedback = useFeedback((s) => s.send)
  const { agencyId } = useAgencyId()

  const params = ls.map(limitAndOffset).map(defaultSort(sortByDefault))
  isTeamLead && params.set("admin", "true")

  const { data: agencyClients, status: agencyClientsStatus } = useQuery(
    ["agency-clients", agencyId, params.toString()],
    fetchAgencyClients,
    { enabled: !isTeamLead && !!agencyId },
  )

  const { data: clientConfigs, status: clientConfigsStatus } = useQuery(
    ["client-configs", params.toString()],
    fetchClientAgencyConfigs,
    { enabled: isTeamLead },
  )

  const [activateClient] = useMutation(activateAgencyClient, {
    onSuccess: () => queryCache.invalidateQueries("agency-clients"),
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(t(e.message), "error")
    },
  })
  const [deactivateClient] = useMutation(deactivateAgencyClient, {
    onSuccess: () => queryCache.invalidateQueries("agency-clients"),
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(t(e.message), "error")
    },
  })

  const onActivate = useCallback(
    (clientId: string) => {
      !!agencyId && activateClient({ agencyId: agencyId, clientId })
    },
    [activateClient, agencyId],
  )

  const onDeactivate = useCallback(
    (clientId: string) => {
      !!agencyId && deactivateClient({ agencyId: agencyId, clientId })
    },
    [deactivateClient, agencyId],
  )

  const status = !isTeamLead ? agencyClientsStatus : clientConfigsStatus
  const data = !isTeamLead ? agencyClients : clientConfigs

  const isLoading = status === "loading" || status === "idle"
  const clients = data?.data
  const total = data?.total
  const clientCols = clientColumns(onActivate, onDeactivate, hasAuthority)
  const configCols = clientConfigColumns(onActivate, onDeactivate, hasAuthority)

  return (
    <Container>
      <Breadcrumbs section={t("nav.admin")} current={t("nav.agency.clients")} />
      <Header label={t("nav.agency.clients")}>
        {isTeamLead ? null : <SelectAgency />}
      </Header>
      <Surface className={styles.surface}>
        {!isTeamLead ? (
          <Table<Client>
            id="clients-list-table"
            columns={clientCols}
            data={clients ?? []}
            filters={[]}
            total={total || 0}
            loading={isLoading}
            getRowProps={getClientRowProps}
            searchBy="search"
            noResults={
              <EmptyState
                title={t("clients.noresults.title")}
                icon={<ClientsIcon />}
              />
            }
          />
        ) : (
          <Table<ClientAgencyConfig>
            id="clientAgencyConfig-list-table"
            columns={configCols}
            data={clients ?? []}
            filters={[]}
            total={total ?? 0}
            loading={isLoading}
            getRowProps={getConfigRowProps}
            searchBy="search"
            noResults={
              <EmptyState
                title={t("clients.noresults.title")}
                icon={<ClientsIcon />}
              />
            }
          />
        )}
      </Surface>
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </Container>
  )
}

const styles = {
  surface: css`
    padding: var(--spacing-xxl-2) var(--spacing-m) var(--spacing-m);
  `,
}

function getClientRowProps(row: Row<Client>) {
  const id = row.original.id
  const configId = row.original.configId
  return {
    onClick: () => history.push(`/agency-clients/${id}/profile/${configId}`),
  }
}

function getConfigRowProps(row: Row<ClientAgencyConfig>) {
  const id = row.original.clientId
  const configId = row.original.id
  const agencyId = row.original.agencyId

  return {
    onClick: () =>
      history.push({
        pathname: `/agency-clients/${id}/profile/${configId}`,
        search: `?agencyId=${agencyId}`,
      }),
  }
}
